import React from 'react';

import { useIntl } from 'react-intl-next';

import { aiMessages } from '@atlaskit/editor-common/ai-messages';
import { useEditorToolbar } from '@atlaskit/editor-common/toolbar';
import {
	AICommandIcon,
	AISpellcheckIcon,
	ToolbarDropdownItem,
	ToolbarDropdownItemSection,
} from '@atlaskit/editor-toolbar';
import type { RegisterComponent } from '@atlaskit/editor-ui-control-model';
import AiGenerativeCleanupIcon from '@atlaskit/icon-lab/core/ai-generative-cleanup';
import type { NewCoreIconProps } from '@atlaskit/icon/types';

import { createOpenAIModalCommand } from '../../pm-plugins/decoration/actions';
import type { AIGlobalOptIn } from '../../types/types';
import type { EditorPluginAIConfigItem } from '../prebuilt/config-items/config-items';
import {
	fixSpellingAndGrammar,
	improveWriting,
} from '../prebuilt/config-items/prompts/enhance/enhance';
import { addPolish } from '../prebuilt/config-items/prompts/polish/polish';

type AIPasteMenuItemProps = {
	aiGlobalOptIn: AIGlobalOptIn;
	configItem: EditorPluginAIConfigItem;
	icon: {
		(props: NewCoreIconProps): JSX.Element;
		displayName: string;
	};
	testId: string;
};

const AIPasteMenuItem = ({ aiGlobalOptIn, configItem, icon, testId }: AIPasteMenuItemProps) => {
	// TODO: EDITOR-5468 - Replace this with purpose built paste menu context
	const { editorView } = useEditorToolbar();
	const { formatMessage, locale } = useIntl();

	const Icon = icon as React.ComponentType<NewCoreIconProps>;
	const label = formatMessage(configItem.title);

	return (
		<ToolbarDropdownItem
			testId={testId}
			elemBefore={<Icon size="small" label="" />}
			onClick={(event) => {
				if (!editorView) {
					return;
				}
				const isKeyboardTrigger = 'key' in event;
				const triggerMethod = isKeyboardTrigger ? 'keyboard' : 'click';
				const openAIModalCommand = createOpenAIModalCommand({
					state: editorView.state,
					configItem,
					lastTriggeredFrom: 'floatingToolbar',
					triggerMethod,
					aiGlobalOptIn,
					locale,
				});
				openAIModalCommand(editorView.state, editorView.dispatch);
			}}
		>
			{label}
		</ToolbarDropdownItem>
	);
};

const AIPasteMenuSection = ({ children }: { children?: React.ReactNode }) => {
	const { formatMessage } = useIntl();
	return (
		<ToolbarDropdownItemSection title={formatMessage(aiMessages.pasteMenuActionsTitle)}>
			{children}
		</ToolbarDropdownItemSection>
	);
};

export const AI_PASTE_MENU_KEY = 'ai-paste-menu';
const AI_PASTE_MENU_SECTION_KEY = 'ai-paste-menu-section';
const AI_PASTE_MENU_IMPROVE_WRITING_KEY = 'ai-paste-menu-improve-writing';
const AI_PASTE_MENU_FIX_SPELLING_KEY = 'ai-paste-menu-fix-spelling-and-grammar';
const AI_PASTE_MENU_ADD_POLISH_KEY = 'ai-paste-menu-add-polish';

export const getAIPasteMenuComponents = ({
	aiGlobalOptIn,
}: {
	aiGlobalOptIn: AIGlobalOptIn;
}): RegisterComponent[] => [
	{ type: 'menu', key: AI_PASTE_MENU_KEY },
	{
		type: 'menu-section',
		key: AI_PASTE_MENU_SECTION_KEY,
		parents: [{ type: 'menu', key: AI_PASTE_MENU_KEY, rank: 1 }],
		component: AIPasteMenuSection,
	},
	{
		type: 'menu-item',
		key: AI_PASTE_MENU_IMPROVE_WRITING_KEY,
		parents: [{ type: 'menu-section', key: AI_PASTE_MENU_SECTION_KEY, rank: 1 }],
		component: () => (
			<AIPasteMenuItem
				aiGlobalOptIn={aiGlobalOptIn}
				configItem={improveWriting()}
				icon={AICommandIcon}
				testId="ai-paste-menu__improve-writing"
			/>
		),
	},
	{
		type: 'menu-item',
		key: AI_PASTE_MENU_FIX_SPELLING_KEY,
		parents: [{ type: 'menu-section', key: AI_PASTE_MENU_SECTION_KEY, rank: 2 }],
		component: () => (
			<AIPasteMenuItem
				aiGlobalOptIn={aiGlobalOptIn}
				configItem={fixSpellingAndGrammar()}
				icon={AISpellcheckIcon}
				testId="ai-paste-menu__fix-spelling-and-grammar"
			/>
		),
	},
	{
		type: 'menu-item',
		key: AI_PASTE_MENU_ADD_POLISH_KEY,
		parents: [{ type: 'menu-section', key: AI_PASTE_MENU_SECTION_KEY, rank: 3 }],
		component: () => (
			<AIPasteMenuItem
				aiGlobalOptIn={aiGlobalOptIn}
				configItem={addPolish()}
				icon={AiGenerativeCleanupIcon}
				testId="ai-paste-menu__add-polish"
			/>
		),
	},
];
